import { SearchIcon } from "@chakra-ui/icons";
import {
  Box,
  Flex,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Text,
} from "@chakra-ui/react";
// import { jobPayloadState } from "_state/atoms/AppAtoms";
import { useQueryClient } from "@tanstack/react-query";
import { jobPayloadState, savedJobPayloadState } from "_state/atoms/AppAtoms";
import { zipCodes } from "components/jobs/zipCodes";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useRecoilState } from "recoil";
import { getZipList, searchZipPos } from "utils/jobs";
import { SecondaryIconButton } from "../HJSButton";
import ZipAlert from "./ZipAlert";
import { useGetUserProfileQuery } from "queries";

interface IDynamicSearchBarProps {
  Icon?: any;
  placeholder?: string;
  width?: string;
  field?: string;
}

const transitionStyle = {
  transition: "all 0.5s",
  borderTopRightRadius: "10px",
  borderBottomRightRadius: "10px",
};

const zipLength = 5;

// memoize the suggestion list
const listCache: any = {};

const DynamicSearchBar = ({
  Icon = <SearchIcon className="fill-white" />,
  placeholder = "Keywords",
  width = "w-24",
  field = "search",
}: IDynamicSearchBarProps): JSX.Element => {
  const location = useLocation();
  const isJobSearch = location.pathname === "/jobs/search";
  const queryClient = useQueryClient();

  const [payload, setPayload] = useRecoilState(jobPayloadState);
  const [savedJobPayload, setSavedJobPayload] =
    useRecoilState(savedJobPayloadState);

  const inputRef = useRef<any>();
  const [isExpand, setExpand] = useState(false);
  const [suggestionList, setSuggestion] = useState<number[]>([]);
  const [inputValue, setInputValue] = useState();
  const [zipError, setZipError] = useState<string>("");

  const { data: userDetails } = useGetUserProfileQuery();

  const handlePayload = useCallback((val: any) => {
    if (
      field === "zipcode" &&
      val?.length === zipLength &&
      zipError.length > 0
    ) {
      isJobSearch
        ? setPayload((prev: any) => ({
            ...prev,
            [field]: val,
          }))
        : setSavedJobPayload((prev: any) => ({
            ...prev,
            [field]: val,
          }));
      setSuggestion([]);
    } else if (field === "search") {
      if (isJobSearch) {
        setPayload((prev: any) => ({
          ...prev,
          [field]: val,
        }));
      } else
        setSavedJobPayload((prev: any) => ({
          ...prev,
          [field]: val,
        }));
    }
  }, []);

  // debouncing
  useEffect(() => {
    // console.log("debounce use effect");

    const timeout = setTimeout(() => {
      // console.log("value : ", inputValue);
      if (inputValue !== undefined) handlePayload(inputValue);
      // console.log("timeout");zipcode
    }, 1000);

    return () => clearTimeout(timeout);
  }, [inputValue]);

  const handleChange = useCallback((e: any): void => {
    const val = inputRef?.current?.value;
    e.stopPropagation();

    if (field === "zipcode" && !isNaN(Number(val))) {
      if (Number(val) <= 0) {
        setSuggestion([]);
        return;
      }

      // value is in the cache don't execute any
      // function just update the state

      if (listCache[val] === undefined) {
        const foundIdx = searchZipPos(
          zipCodes,
          Number(val) * Math.pow(10, zipLength - val.length)
        );
        const sugesstions = getZipList({
          codes: zipCodes,
          targetIdx: foundIdx,
          value: val,
        });
        listCache[val] = sugesstions;
        setSuggestion(sugesstions);
      } else {
        setSuggestion(listCache[val]);
      }

      if (Array.isArray(listCache[val]) && listCache[val]?.length <= 0)
        setZipError("Not a valid zip code");
      else if (Array.isArray(listCache[val]) && listCache[val]?.length > 0) {
        setZipError("");
      }
    }
  }, []);

  // reset the fields a.t. change in payload store
  useEffect(() => {
    if ((inputRef?.current as boolean) && field === "search") {
      inputRef.current.value = isJobSearch
        ? payload.search ?? null
        : savedJobPayload.search ?? null;
      setInputValue(inputRef?.current?.value);
    } else if (field === "zipcode") {
      inputRef.current.value = userDetails?.zipcode;
      zipError?.length > 0 && setZipError("");
    }
  }, [field, isJobSearch, payload, savedJobPayload, userDetails]);

  const [isOpen, setOpen] = useState(false);
  const [zipAlertOpen, setZipAlertOpen] = useState<boolean>(false);

  useEffect(() => {
    suggestionList.length > 0 && setOpen(true);
  }, [suggestionList]);

  return (
    <>
      <ZipAlert
        isOpen={zipAlertOpen}
        onClose={() => setZipAlertOpen(false)}
        zipRef={inputRef}
      />
      <Box>
        <Flex background={isExpand ? "teal.600" : "initial"} borderRadius="5px">
          <SecondaryIconButton
            border="none"
            aria-label="Search jobs"
            icon={Icon}
            onClick={() => setExpand((prev) => !prev)}
          />
          <Popover
            initialFocusRef={inputRef}
            // returnFocusOnClose={true}
            isOpen={isOpen}
            onClose={() => setOpen(false)}
            placement="bottom"
            closeOnBlur={true}
            // matchWidth={true}
            openDelay={0}
            offset={[-5, 0]}
          >
            <PopoverTrigger>
              <input
                type={field === "zipcode" ? "number" : "text"}
                ref={inputRef}
                placeholder={placeholder}
                className={`${
                  isExpand ? `${width} visible` : `w-0 invisible`
                }  !bg-teal-700 outline-none border-0 text-white pr-1 placeholder:text-white`}
                style={transitionStyle}
                onKeyUp={(e: any) => {
                  setInputValue(e.target.value);
                  field === "zipcode" && handleChange(e);
                }}
                onKeyDown={(e) => {
                  if (
                    field === "zipcode" &&
                    inputRef?.current?.value?.length >= 5 &&
                    (Number(e.key) >= 0 || Number(e.key) <= 9)
                  ) {
                    e.preventDefault();
                  }
                }}
                // onChange={(e: any) => {
                //   setInputValue(e.target.value);
                // }}
                inputMode={field === "zipcode" ? "numeric" : "text"}
                maxLength={field === "zipcode" ? zipLength : undefined}
              />
            </PopoverTrigger>
            {suggestionList.length > 0 && (
              <PopoverContent
                className="overflow-auto h-fit"
                maxH={28}
                background="teal.600"
                width="initial"
                py={2}
                px={4}
              >
                {suggestionList.map((el, idx) => (
                  <Text
                    color="white"
                    key={idx}
                    cursor="pointer"
                    _hover={{ backgroundColor: "hjsSecondaryHover" }}
                    onClick={(e) => {
                      inputRef.current.value = el;
                      isJobSearch && setZipAlertOpen(true);
                      // ? setPayload((prev: any) => ({
                      //     ...prev,
                      //     zipcode: el,
                      //   }))
                      // : setSavedJobPayload((prev: any) => ({
                      //     ...prev,
                      //     zipcode: el,
                      //   }));
                      setSuggestion([]);
                    }}
                  >
                    {el}
                  </Text>
                ))}
              </PopoverContent>
            )}
          </Popover>
        </Flex>

        {zipError.length > 0 && (
          <Box position="relative">
            <Text
              position="absolute"
              fontSize={12}
              color="red"
              ml={1}
              fontWeight={600}
            >
              {zipError}
            </Text>
          </Box>
        )}
      </Box>
    </>
  );
};

export default React.memo(DynamicSearchBar);
